import {includes, isEmpty, map} from 'lodash';
import {useTranslation} from 'react-i18next';

import {
    Accordion, AccordionDetails, AccordionShortDetails, AccordionTitle,
    Checkbox, CopyText, DataWrapper, Icon, Toggle
} from 'components';
import {CLIENT_CONNECT_AUTO_REMOVE_TRANSLATE} from 'constants/client-connect';
import {ClientConnectAutoRemove} from 'enums';
import {Group} from 'interfaces';
import {TRANSLATION_KEYS} from 'locale/translation-keys';
import {getAssignmentName} from 'services';
import {ClientConnect, UserSummary} from 'types';
import {findUserName, getParticipantName} from 'utils/transform';

const T_KEYS = TRANSLATION_KEYS.USER.ACCOUNT.CLIENTS_CONNECTIONS;

type Props = {
    loading?: boolean;
    connects: ClientConnect[];
    groups: Group[];
    selectedIds: string[];
    toggleDisabledInProgress: string[];
    users: UserSummary[];
    onUpdate: (connect: ClientConnect) => void;
    onDelete: (connect: ClientConnect) => void;
    onSendMessage: (connect: ClientConnect) => void;
    onToggleDisabled: (connect: ClientConnect) => void;
    onToggleSelection: (id: string) => void;
    isSendMessageAvailable: (connect: ClientConnect) => boolean;
    onOpenScheduledMessageDetailsDialog: (connect: ClientConnect) => void;
};

export const ClientConnectAccordion = ({
    connects, selectedIds, loading, groups, toggleDisabledInProgress, users, onSendMessage, onUpdate, onDelete,
    onToggleSelection, onOpenScheduledMessageDetailsDialog, onToggleDisabled, isSendMessageAvailable
}: Props): any => {
    const {t} = useTranslation();
    return (
        <DataWrapper isLoading={loading} noData={isEmpty(connects)}>
            {map(connects, (connect: ClientConnect) => (
                <Accordion key={connect.id} className='tw-mb-2 lg:tw-hidden'>
                    <AccordionTitle>
                        <div className='tw-flex tw-items-center tw-justify-between'>
                            <div className='tw-text-break-word'>{connect.userInfo.id}</div>
                            <div className='tw-flex tw-justify-end'>
                                {isSendMessageAvailable(connect) &&
                                    <Icon
                                        button
                                        group
                                        name='reply'
                                        color='primary'
                                        stopPropagation
                                        onClick={() => onSendMessage(connect)}
                                    />}
                                <Icon
                                    button
                                    group
                                    name='edit'
                                    color='primary'
                                    stopPropagation
                                    onClick={() => onUpdate(connect)}
                                />
                                <Icon
                                    button
                                    group
                                    name='delete'
                                    color='alert'
                                    stopPropagation
                                    onClick={() => onDelete(connect)}
                                />
                            </div>
                        </div>
                    </AccordionTitle>
                    <AccordionShortDetails showExpanded className='tw-relative tw-min-h-6'>
                        <div className='tw-absolute'>
                            <Checkbox
                                name={`checkbox-${connect.id}`}
                                checked={selectedIds.includes(connect.id)}
                                onChange={() => onToggleSelection(connect.id)}
                            />
                        </div>
                        {(connect.userInfo.firstName || connect.userInfo.lastName) &&
                            <div className='tw-flex tw-ml-10 tw-mb-2'>
                                <Icon name='info' />
                                <div className='tw-ml-2 tw-break-all'>
                                    {getParticipantName(connect.userInfo)}
                                </div>
                            </div>}
                        {connect.contacts.phone && (
                            <div className='tw-flex tw-ml-10 tw-mb-2'>
                                <Icon name='phone' className='tw-col-start-2' />
                                <div className='tw-ml-2 tw-break-all'>{connect.contacts.phone}</div>
                            </div>
                        )}
                        {connect.contacts.email && (
                            <div className='tw-flex tw-ml-10 tw-mb-2'>
                                <Icon name='mail' className='tw-col-start-2' />
                                <div className='tw-ml-2 tw-break-all'>{connect.contacts.email}</div>
                            </div>
                        )}
                    </AccordionShortDetails>
                    <AccordionDetails>
                        {connect.assignmentInfo.groupId && (
                            <div className='tw-flex tw-ml-10 tw-mb-2'>
                                <Icon name='paper-clip' className='tw-col-start-2' />
                                <div className='tw-ml-2 tw-break-all'>
                                    {getAssignmentName(
                                        groups,
                                        connect.assignmentInfo.groupId,
                                        connect.assignmentInfo.subgroupId
                                    )}
                                </div>
                            </div>
                        )}
                        {connect.agent?.email &&
                            <div className='tw-flex tw-ml-10 tw-mb-2'>
                                <Icon name='agent-participant' className='tw-col-start-2' />
                                <div className='tw-ml-2 tw-break-all'>
                                    {findUserName(t, users, {email: connect.agent?.email}) || connect.agent?.email}
                                </div>
                            </div>}
                        {connect.comments && (
                            <div className='tw-flex tw-ml-10 tw-mb-2'>
                                <Icon name='calendar' className='tw-col-start-2' />
                                <div className='tw-ml-2 tw-break-all'>{connect.comments}</div>
                            </div>
                        )}
                        {connect.scheduledMessages.length > 0 && (
                            <div className='tw-flex tw-ml-10 tw-mb-2'>
                                <Icon name='check-square' className='tw-col-start-2' />
                                <div className='tw-flex tw-ml-2 tw-break-all'>
                                    {t(T_KEYS.FIELDS.SCHEDULED_MESSAGES)}
                                    {' '}
                                    {connect.scheduledMessages.length}
                                    <Icon
                                        name='info'
                                        color='default-dimmed'
                                        className='tw-ml-1'
                                        onClick={() => onOpenScheduledMessageDetailsDialog(connect)}
                                    />
                                </div>
                            </div>
                        )}
                        {connect.link && (
                            <div className='tw-flex tw-ml-10 tw-mb-2'>
                                <CopyText text={connect.link} iconOnly iconColor='default' />
                                <div className='tw-ml-2 tw-break-all'>{connect.link}</div>
                            </div>
                        )}
                        <div className='tw-flex tw-items-center tw-justify-between tw-ml-16 tw-mb-2'>
                            <div className='tw-mr-2 tw-ml-1'>{t(T_KEYS.FIELDS.ENABLED)}</div>
                            <Toggle
                                name={`toggle-${connect.id}`}
                                checked={!connect.disabled}
                                disabled={includes(toggleDisabledInProgress, connect.id)}
                                onChange={() => onToggleDisabled(connect)}
                            />
                        </div>
                        {connect.autoRemove !== ClientConnectAutoRemove.Never && (
                            <div className='tw-flex tw-items-center tw-justify-between tw-ml-16 tw-mb-2'>
                                <div className='tw-mr-2 tw-ml-1'>{t(T_KEYS.FIELDS.AUTO_REMOVE)}</div>
                                <div className='tw-text-default-dimmed'>
                                    {t(CLIENT_CONNECT_AUTO_REMOVE_TRANSLATE[connect.autoRemove])}
                                </div>
                            </div>
                        )}
                    </AccordionDetails>
                </Accordion>
            ))}
        </DataWrapper>
    );
};